<template>
	<div :style="isScrolled ? {
		position: 'sticky',
		top: contractHeaderHeight+'px',
		left: '0',
		right: '0',
		zIndex: 9
	} : {}">
		<v-card class="budget-modal-card mb-2"
			:class="{ 'disable-all-buttons': isEditProhibited }"
			ref="contractSecondHeaderInfo">
		<v-card-text class="py-2">
			<div class="d-flex align-start" style="gap:10px;" v-if="contract" :class="{'flex-column': $vuetify.breakpoint.width < 720}">
				<div class="d-flex flex-column flex-1" :style="$vuetify.breakpoint.width < 720 ? {width: '100%'} : {}">
					<div class="d-flex align-center">
						<p class="mb-0 mr-2 font-weight-bold caption">Estado:</p>
						<v-chip
							:color="contract.last_status?.status | contractStatusColor"
							label
							outlined
							x-small
							class="px-1"
						>
							<b>{{ getContractStatusText(contract.last_status?.status) }}</b>
						</v-chip>

						<ContractPdfHistory 
							:contract="contract"
							:isSubcontract="isSubcontract"
							:contractStatuses="contractStatuses"
						/>

						<!-- Ver observaciones -->
						<v-tooltip bottom v-if="canShowObservationsButton">
							<template v-slot:activator="{ on, attrs }">
								<v-btn
									icon
									dark
									small
									color="grey"
									@click="toggleObservations()"
									v-bind="attrs"
									v-on="on"
								>
									<v-icon dark small>mdi-comment-alert-outline</v-icon>
								</v-btn>
							</template>
							<span>{{showingObservations ? 'Cerrar observaciones del contrato.' : 'Ver las observaciones del contrato.' }}</span>  
						</v-tooltip>
					</div>
				</div>
				<div class="d-flex flex-column align-center" style="gap:5px;" :style="$vuetify.breakpoint.width < 720 ? {width: '100%'} : {}">
					<div class="d-flex flex-column" style="gap:5px;" v-if="contractPreviewing">
						<div>
							<v-tooltip bottom>
								<template v-slot:activator="{ on, attrs }">
									<v-btn
										depressed
										color="#959595"
										outlined
										dark
										small
										@click="previewContract"
										v-bind="attrs"
										v-on="on"
									>
										<v-icon small>mdi-briefcase-edit-outline</v-icon>
										<p class="mb-0 ml-2 caption">{{ backToEditorText }}</p>
									</v-btn>
								</template>
								<span>{{ backToEditorTooltip }}</span>
							</v-tooltip>
						</div>
						<div v-if="$vuetify.breakpoint.width >= 1550">
							<v-tooltip bottom v-if="showPreviewButton && !contractPreviewing && validatePayment">
								<template v-slot:activator="{ on, attrs }">
									<v-btn
										depressed
										color="green"
										dark
										small
										block
										@click="previewContract"
										v-bind="attrs"
										v-on="on"
									>
										<v-icon small>mdi-file-pdf-box</v-icon>
										<p class="mb-0 ml-2 caption">{{ previewAndSendText }}</p>
									</v-btn>
								</template>
								<span>{{ contract.last_status?.status == contractStatuses.OBSERVADO_CONTRAPARTE ? reviewObservationsText : firstSendText }}</span>
							</v-tooltip>
							<v-chip
								v-else-if="!validatePayment"
								small
								label
								class="px-2 py-3"
								style="height: auto; white-space: normal; width: 100%; background-color: #FFF3E0 !important;"
							>
								<v-icon small left color="orange darken-2">mdi-alert-outline</v-icon>
								<span style="font-size: 11.5px; line-height: 1.4; color: #E65100;">Se debe configurar el 100% de los pagos en la tabla de costo</span>
							</v-chip>
							<v-tooltip bottom v-else-if="showPreviewButton && contractPreviewing">
								<template v-slot:activator="{ on, attrs }">
									<v-btn
										depressed
										color="green"
										dark
										small
										block
										:loading="loadingPdf"
										@click="contractToPdf"
										v-bind="attrs"
										v-on="on"
									>
										<v-icon small>mdi-file-pdf-box</v-icon>
										<p class="mb-0 ml-2 caption">{{ sendText }}</p>
									</v-btn>
								</template>
								<span>{{ previewTooltip }}</span>
							</v-tooltip>
						</div>
					</div>
					<div class="d-flex align-center flex-1 justify-end" style="gap: 5px;width: 100%;" v-else>
						<div v-if="showPreviewButton && !contractPreviewing">
							<v-menu
								v-model="menuTemplate"
								:close-on-content-click="false"
								offset-y
							>
								<template v-slot:activator="{ on: menuOn, attrs: menuAttrs }">
									<v-tooltip bottom>
										<template v-slot:activator="{ on: tooltipOn, attrs: tooltipAttrs }">
											<v-btn
												depressed
												color="#959595"
												outlined
												small
												v-bind="{ ...tooltipAttrs, ...menuAttrs }"
												v-on="{ ...tooltipOn, ...menuOn }"
											>
												<v-icon size="20">mdi-text-box-multiple-outline</v-icon>
											</v-btn>
										</template>
										<span>Plantillas</span>
									</v-tooltip>
								</template>
								<v-card style="min-width: 300px;">
									<v-card-text class="px-3 pt-4 pb-3">
										<CompanyTemplatesHandler
											:type="'contract'"
											:itemContent="contract.sections"
											color="#959595"
											@template-content-selected="templateContentSelected"/>
									</v-card-text>
								</v-card>
							</v-menu>
						</div>
						<div @click.capture="toProjectTable">
							<v-tooltip bottom>
								<template v-slot:activator="{ on, attrs }">
									<v-btn
										depressed
										color="#959595"
										outlined
										dark
										small
										:to="{
											name: 'ProjectCostBreakdown', 
											params: { id: contract.morphable?.id } 
										}"
										v-bind="attrs"
										v-on="on"
									>
										<v-icon>mdi-currency-usd</v-icon>
									</v-btn>
								</template>
								<span>Ver Tabla de Costo del Proyecto</span>
							</v-tooltip>
						</div>
						<div v-if="!isAddendum && !isSubcontract">
							<v-menu offset-y>
								<template v-slot:activator="{ on: menuOn, attrs: menuAttrs }">
									<v-tooltip bottom>
										<template v-slot:activator="{ on: tooltipOn, attrs: tooltipAttrs }">
											<v-btn
												depressed
												color="#959595"
												outlined
												small
												dark
												v-bind="{ ...menuAttrs, ...tooltipAttrs }"
												v-on="{ ...menuOn, ...tooltipOn }"
											>
												<v-icon>mdi-briefcase-plus-outline</v-icon>
											</v-btn>
										</template>
										<span>Anexos de Contrato</span>
									</v-tooltip>
								</template>
								<v-list dense class="py-0" style="min-width: 300px">
									<v-list-item dense class="mb-1 px-1" style="min-height: 25px; background: #959595;">
										<div class="d-flex justify-space-between align-center w-100 px-1 mx-1">
											<p class="caption mb-0 font-weight-bold white--text">{{ isAddendum ? 'Subcontratos Asociados' : isSubcontract ? 'Anexos Asociados' : 'Anexos de Contrato' }}</p>
											<div class="ml-6 d-flex justify-end align-center">
												<v-tooltip bottom v-if="!isAddendum && !isSubcontract">
													<template v-slot:activator="{ on, attrs }">
														<v-btn
															icon
															color="white"
															class="mb-0"
															small
															:dark="contract.last_status?.status == contractStatuses.FIRMADO_AMBAS_PARTES"
															:disabled="contract.last_status?.status < contractStatuses.FIRMADO_AMBAS_PARTES"
															@click="startContractAddendum"
															v-bind="attrs"
															v-on="on"
														>
															<v-icon small>mdi-plus</v-icon>
														</v-btn>
													</template>
													<span>Crear anexo de contrato</span>
												</v-tooltip>
											</div>
										</div>
									</v-list-item>
									<v-list-item dense class="mb-0 px-0 green" style="min-height: 25px; background: #959595;">
										<v-btn-toggle
											v-model="deletedAddendumShow"
											dense
											class="d-flex flex-row mb-0"
											style="width: 100%"
										>
											<v-btn class="flex-grow-1"
												:color="!deletedAddendumShow ? '#FF1209':''"
												@click.stop="toggleDeletedAddendumShow()">
													<p class="mb-0 caption font-weight-bold" :class="!deletedAddendumShow ? 'white--text':''">{{ isAddendum ? 'Subcontratos' : isSubcontract ? 'Anexos' : 'Anexos' }}</p>
											</v-btn>
											<v-btn class="flex-grow-1"
												:color="deletedAddendumShow ? '#FF1209':''"
												@click.stop="toggleDeletedAddendumShow()">
												<div class="d-flex flex-column align-start justify-center"
													style="position: relative;">
															<p class="mb-0" style="font-size: 8px; position:absolute; top: -6px; left: 0px;" :class="deletedAddendumShow ? 'white--text':''">Historial</p>
															<p class="mb-0 caption font-weight-bold" :class="deletedAddendumShow ? 'white--text':''">{{ isAddendum ? 'Subcontratos' : isSubcontract ? 'Anexos' : 'Anexos' }} Eliminados</p>
												</div>
											</v-btn>
										</v-btn-toggle>
									</v-list-item>
									<v-list-item-group color="#524D4D" v-if="contractAddendums.length">
										<v-list-item
											v-for="(addendum, index) in contractAddendums"
											:key="'ass-'+index"
											dense
											@click="$store.commit('contracts/setContractAddendumComponentModalId', addendum.id)"
											style="min-width: 200px"
										>
											<v-list-item-title dense>
												<div class="d-flex justify-space-between align-center item-background-hover">
													<div class="d-flex align-center justify-space-between caption py-1" style="width: 100%" v-if="deletedAddendumShow">
														<div>
															<div style="width: 90px">Ir a {{ isAddendum ? 'Subcontrato' : isSubcontract ? 'Anexo' : 'Anexo' }}</div>
															<p class="caption mb-0">Eliminado: {{ addendum.deleted_at | toDate  }}</p>
														</div>
														<div class="text-end">
															<b class="text-end" style="flex: 1;">#{{addendum.addendum_index}}</b>
															<p class="caption mb-0">{{ addendum.created_at | toDate }}</p>
														</div>
													</div>
													<div class="d-flex align-center justify-space-between caption py-1" style="width: 100%" v-else>
														<div style="width: 90px">Ir a {{ isAddendum ? 'Subcontrato' : isSubcontract ? 'Anexo' : 'Anexo' }}</div>
														<div class="text-end">
															<b class="text-end" style="flex: 1;">#{{addendum.addendum_index}}</b>
															<p class="caption mb-0">{{ addendum.created_at | toDate }}</p>
														</div>
													</div>
												</div>
											</v-list-item-title>
										</v-list-item>
									</v-list-item-group>
									<v-list-item v-else>
										<div class="d-flex justify-center align-center caption">
											<p class="mb-0">{{ isAddendum ? 'No hay subcontratos asociados a este anexo' : isSubcontract ? 'No hay anexos asociados a este subcontrato' : 'No hay anexos asociados a este contrato' }}</p>
										</div>
									</v-list-item>
								</v-list>
							</v-menu>
						</div>
						<div v-if="isAddendum">
							<v-tooltip bottom>
								<template v-slot:activator="{ on, attrs }">
									<v-btn
										depressed
										color="error"
										dark
										small
										@click="startDeleteContract"
										v-bind="attrs"
										v-on="on"
									>
										<v-icon>mdi-delete-outline</v-icon>
									</v-btn>
								</template>
								<span>Eliminar Anexo</span>
							</v-tooltip>
						</div>
						<div v-if="!isSubcontract">
							<AdditionalRequirementsMenu
								:contract="contract"
								:project="project"
							/>
						</div>
						<ProjectLineDocumentsMenu :project_line_id="project?.project_line_info?.project_line_id" :size="'small'"/>
						<v-tooltip bottom>
							<template v-slot:activator="{ on, attrs }">
								<v-btn
									depressed
									color="#959595"
									outlined
									dark
									small
									v-if="contract && contract.pdf"
									@click="openContractPDF"
									v-bind="attrs"
									v-on="on"
								>
									<v-icon>mdi-file-pdf-box</v-icon>
								</v-btn>
							</template>
							<span>{{ pdfTooltip }}</span>
						</v-tooltip>
						<div v-if="$vuetify.breakpoint.width >= 1550">
							<v-tooltip bottom v-if="showPreviewButton && !contractPreviewing && validatePayment">
								<template v-slot:activator="{ on, attrs }">
									<v-btn
										depressed
										color="green"
										dark
										small
										@click="previewContract"
										v-bind="attrs"
										v-on="on"
									>
										<v-icon small>mdi-file-pdf-box</v-icon>
										<p class="mb-0 ml-2 caption">{{ previewAndSendText }}</p>
									</v-btn>
								</template>
								<span>{{ contract.last_status?.status == contractStatuses.OBSERVADO_CONTRAPARTE ? reviewObservationsText : firstSendText }}</span>
							</v-tooltip>
							<v-chip
								v-else-if="!validatePayment"
								small
								label
								class="px-2 py-3"
								style="height: auto; white-space: normal; width: 100%; background-color: #FFF3E0 !important;"
							>
								<v-icon small left color="orange darken-2">mdi-alert-outline</v-icon>
								<span style="font-size: 11.5px; line-height: 1.4; color: #E65100;">Se debe configurar el 100% de los pagos en la tabla de costo</span>
							</v-chip>
							<v-tooltip bottom v-else-if="showPreviewButton && contractPreviewing">
								<template v-slot:activator="{ on, attrs }">
									<v-btn
										depressed
										color="green"
										dark
										small
										:loading="loadingPdf"
										@click="contractToPdf"
										v-bind="attrs"
										v-on="on"
									>
										<v-icon small>mdi-file-pdf-box</v-icon>
										<p class="mb-0 ml-2 caption">{{ sendText }}</p>
									</v-btn>
								</template>
								<span>{{ previewTooltip }}</span>
							</v-tooltip>
						</div>
					</div>

					<div class="d-flex flex-column" style="gap: 5px;width: 100%;">
						<v-tooltip bottom v-if="canSignCompany">
							<template v-slot:activator="{ on, attrs }">
								<v-btn
									depressed
									color="green"
									dark
									block
									small
									@click="companyRepSign"
									v-bind="attrs"
									v-on="on"
								>
									<v-icon small>mdi-signature-freehand</v-icon>
									<p class="mb-0 ml-2 caption">Firmar {{ contractTypeText }}</p>
								</v-btn>
							</template>
							<span>{{ signTooltip }}</span>
						</v-tooltip>
						<div v-if="$vuetify.breakpoint.width < 1550">
							<v-tooltip bottom v-if="showPreviewButton && !contractPreviewing && validatePayment">
								<template v-slot:activator="{ on, attrs }">
									<v-btn
										depressed
										color="green"
										dark
										small
										block
										@click="previewContract"
										v-bind="attrs"
										v-on="on"
									>
										<v-icon small>mdi-file-pdf-box</v-icon>
										<p class="mb-0 ml-2 caption">{{ previewAndSendText }}</p>
									</v-btn>
								</template>
								<span>{{ contract.last_status?.status == contractStatuses.OBSERVADO_CONTRAPARTE ? reviewObservationsText : firstSendText }}</span>
							</v-tooltip>
							<v-chip
								v-else-if="!validatePayment"
								small
								label
								class="px-2 py-3"
								style="height: auto; white-space: normal; width: 100%; background-color: #FFF3E0 !important;"
							>
								<v-icon small left color="orange darken-2">mdi-alert-outline</v-icon>
								<span style="font-size: 11.5px; line-height: 1.4; color: #E65100;">Se debe configurar el 100% de los pagos en la tabla de costo</span>
							</v-chip>
							<v-tooltip bottom v-else-if="showPreviewButton && contractPreviewing">
								<template v-slot:activator="{ on, attrs }">
									<v-btn
										depressed
										color="green"
										dark
										block
										small
										:loading="loadingPdf"
										@click="contractToPdf"
										v-bind="attrs"
										v-on="on"
									>
										<v-icon small>mdi-file-pdf-box</v-icon>
										<p class="mb-0 ml-2 caption">{{ sendText }}</p>
									</v-btn>
								</template>
								<span>{{ previewTooltip }}</span>
							</v-tooltip>
						</div>
						<v-tooltip bottom v-if="isSubcontract || isAddendum">
							<template v-slot:activator="{ on, attrs }">
								<v-btn
									outlined
									dark
									block
									small
									color="#959595"
									@click="toParentContract"
									v-bind="attrs"
									v-on="on"
								>
									<v-icon dark small class="mr-2">mdi-briefcase-outline</v-icon>
									<p class="mb-0 caption">Ver Contrato</p>
								</v-btn>
							</template>
							<span>Ver Contrato Asociado</span>
						</v-tooltip>
						<v-menu offset-y v-if="otherContractAddendums && isAddendum && !fromModal">
							<template v-slot:activator="{ on: menuOn, attrs: menuAttrs }">
								<v-tooltip bottom>
									<template v-slot:activator="{ on: tooltipOn, attrs: tooltipAttrs }">
										<v-btn
											outlined
											dark
											block
											small
											color="#959595"
											class="d-flex align-center justify-space-between"
											v-bind="{ ...menuAttrs, ...tooltipAttrs }"
											v-on="{ ...menuOn, ...tooltipOn }"
										>
											<div class="d-flex align-center justify-center flex-grow-1">
												<v-icon dark small class="mr-2">mdi-briefcase-plus-outline</v-icon>
												<p class="mb-0 caption">Otros Anexos</p>
											</div>
											<v-icon dark small class="ml-4">mdi-plus</v-icon>
										</v-btn>
									</template>
									<span>Ver Otros Anexos del Contrato</span>
								</v-tooltip>
							</template>
							<v-list dense class="py-0" style="min-width: 300px">
								<v-list-item dense class="mb-1 px-1" style="min-height: 25px; background: #959595;">
									<div class="d-flex justify-space-between align-center w-100 px-1 mx-1">
										<p class="caption mb-0 font-weight-bold white--text">Anexos de Contrato</p>
										<div class="ml-6 d-flex justify-end align-center">
											<v-tooltip bottom>
												<template v-slot:activator="{ on, attrs }">
													<v-btn
														icon
														color="white"
														class="mb-0"
														small
														@click="startContractAddendum"
														v-bind="attrs"
														v-on="on"
													>
														<v-icon small>mdi-plus</v-icon>
													</v-btn>
												</template>
												<span>Crear anexo de contrato</span>
											</v-tooltip>
										</div>
									</div>
								</v-list-item>
								<v-list-item dense class="mb-0 px-0 green" style="min-height: 25px; background: #959595;">
									<v-btn-toggle
										v-model="deletedAddendumShow"
										dense
										class="d-flex flex-row mb-0"
										style="width: 100%"
									>
										<v-btn class="flex-grow-1"
											:color="!deletedAddendumShow ? '#FF1209':''"
											@click.stop="toggleDeletedAddendumShow()">
											<p class="mb-0 caption font-weight-bold" :class="!deletedAddendumShow ? 'white--text':''">Anexos</p>
										</v-btn>
										<v-btn class="flex-grow-1"
											:color="deletedAddendumShow ? '#FF1209':''"
											@click.stop="toggleDeletedAddendumShow()">
											<div class="d-flex flex-column align-start justify-center"
												style="position: relative;">
												<p class="mb-0" style="font-size: 8px; position:absolute; top: -6px; left: 0px;" :class="deletedAddendumShow ? 'white--text':''">Historial</p>
												<p class="mb-0 caption font-weight-bold" :class="deletedAddendumShow ? 'white--text':''">Anexos Eliminados</p>
											</div>
										</v-btn>
									</v-btn-toggle>
								</v-list-item>
								<v-list-item-group color="#524D4D" v-if="contractAddendumsList.length">
									<v-list-item
										v-for="(addendum, index) in contractAddendumsList"
										:key="'ass-'+index"
										dense
										@click="$store.commit('contracts/setContractAddendumComponentModalId', addendum.id)"
										style="min-width: 200px"
									>
										<v-list-item-title dense>
											<div class="d-flex justify-space-between align-center item-background-hover">
												<div class="d-flex align-center justify-space-between caption py-1" style="width: 100%" v-if="deletedAddendumShow">
													<div>
														<div style="width: 90px">Ir a Anexo</div>
														<p class="caption mb-0">Eliminado: {{ addendum.deleted_at | toDate  }}</p>
													</div>
													<div class="text-end">
														<b class="text-end" style="flex: 1;">#{{addendum.addendum_index}}</b>
														<p class="caption mb-0">{{ addendum.created_at | toDate }}</p>
													</div>
												</div>
												<div class="d-flex align-center justify-space-between caption py-1" style="width: 100%" v-else>
													<div style="width: 90px">Ir a Anexo</div>
													<div class="text-end">
														<b class="text-end" style="flex: 1;">#{{addendum.addendum_index}}</b>
														<p class="caption mb-0">{{ addendum.created_at | toDate }}</p>
													</div>
												</div>
											</div>
										</v-list-item-title>
									</v-list-item>
								</v-list-item-group>
								<v-list-item v-else>
									<div class="d-flex justify-center align-center caption">
										<p class="mb-0">No hay anexos asociados a este contrato</p>
									</div>
								</v-list-item>
							</v-list>
						</v-menu>

						<!-- Reestablecer contrato rechazado -->
						<v-tooltip bottom v-if="contract.last_status?.status == contractStatuses.RECHAZADO">
							<template v-slot:activator="{ on, attrs }">
								<v-btn
									dark
									block
									small
									color="green"
									@click="openResetContractModal()"
									v-bind="attrs"
									v-on="on"
								>
									<v-icon dark small class="mr-2">mdi-briefcase-outline</v-icon>
									<p class="mb-0 caption">Restablecer {{ contractTypeText }}</p>
								</v-btn>
							</template>
							<span>Restablecer el {{ contractTypeText }} rechazado. Devuelve el {{ contractTypeText }} a su estado inicial.</span>
						</v-tooltip>
						<!-- <v-tooltip bottom>
							<template v-slot:activator="{ on, attrs }">
								<v-btn
									depressed
									color="#959595"
									dark
									outlined
									small
									v-if="contract && contract.pdf"
									@click="openContractPDF"
									v-bind="attrs"
									v-on="on"
								>
									<v-icon small>mdi-file-pdf-box</v-icon>
									<p class="mb-0 caption">{{viewPdfText}}</p>
								</v-btn>
							</template>
							<span>{{ pdfTooltip }}</span>
						</v-tooltip> -->
					</div>
				</div>
			</div>
		</v-card-text>
	</v-card>
	<AcceptCancelModal
		:acceptRejectModal="showResetContractModal"
		:acceptRejectModalData="resetContractModalData"
		:closeModal="closeResetContractModal"
	/>
	</div>
</template>

<script>
import ContractPdfHistory from '@/components/Helpers/Components/ContractPdfHistory'
import CompanyTemplatesHandler from '@/components/Helpers/Components/CompanyTemplatesHandler'
import AdditionalRequirementsMenu from '@/components/Views/Projects/Requirements/AdditionalRequirementsMenu'
import ProjectLineDocumentsMenu from '@/components/Views/Projects/Parts/ProjectLineDocumentsMenu'
import AcceptCancelModal from '@/components/Modals/General/AcceptCancelModal'
import {addNewStatus} from '@/helpers/api/contracts'

export default {
	name: 'ContractToolbar',
	components: {
		ContractPdfHistory,
		CompanyTemplatesHandler,
		AdditionalRequirementsMenu,
		ProjectLineDocumentsMenu,
		AcceptCancelModal
	},
	props: {
		contract: {
			type: Object,
			required: true
		},
		isEditProhibited: {
			type: Boolean,
			required: false
		},
		contractHeaderHeight: {
			type: Number,
			required: true
		},
		isScrolled: {
			type: Boolean,
			required: true
		},
		contractPreviewing: {
			type: Boolean,
			required: true
		},
		loadingPdf: {
			type: Boolean,
			required: true
		},
		showPreviewButton: {
			type: Boolean,
			default: true
		},
		canSignCompany: {
			type: Boolean,
			default: false
		},
		viewPdfText: {
			type: String,
			default: 'Ver PDF'
		},
		contractAddendums: {
			type: Array,
			default: () => []
		},
		contractStatuses: {
			type: Object,
			default: () => ({})
		},
		otherContractAddendums: {
			type: Array,
			default: () => []
		},
		fromModal: {
			type: Boolean,
			default: false
		},
		paymentValidation: {
			type: Object,
			default: null
		},
		showingObservations: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			deletedAddendumShow: false,
			menuTemplate: false,
			resizeObserver: null,
			showResetContractModal: false,
			pendingResetStatus: null
		}
	},
	computed: {
		project(){
			return this.contract?.morphable
		},
		contractType() {
			if (this.contract?.addendum_to_contract_id) {
				return 'addendum'
			}
			if (this.contract?.subcontract_to_contract_id) {
				return 'subcontract'
			}
			return 'contract'
		},
		contractTypeText() {
			if (this.contract?.addendum_to_contract_id) {
				return 'Anexo'
			}
			if (this.contract?.subcontract_to_contract_id) {
				return 'Subcontrato'
			}
			return 'Contrato'
		},
		statusNotice() {
			if(this.isAddendum){
				return this.$options.filters.addendumStatusNotice(this.contract.last_status?.status) || ''
			}
			else if(this.isSubcontract){
				return this.$options.filters.subcontractStatusNotice(this.contract.last_status?.status) || ''
			}
			return this.$options.filters.contractEstatusNotice(this.contract.last_status?.status) || ''
		},
		isAddendum() {
			return this.contractType === 'addendum'
		},
		isSubcontract() {
			return this.contractType === 'subcontract'
		},
		backToEditorText() {
			return 'Volver a Editor de Cláusulas'
		},
		backToEditorTooltip() {
			const typeTexts = {
				contract: 'Vuelve a editar las claúsulas del contrato',
				addendum: 'Vuelve a editar las claúsulas del anexo',
				subcontract: 'Vuelve a editar las claúsulas del subcontrato'
			}
			return typeTexts[this.contractType]
		},
		previewAndSendText() {
			const typeTexts = {
				contract: 'Previsualizar y Enviar Contrato',
				addendum: 'Previsualizar y Enviar Anexo',
				subcontract: 'Previsualizar y Enviar Subcontrato'
			}
			return typeTexts[this.contractType]
		},
		sendText() {
			const typeTexts = {
				contract: 'Enviar Contrato',
				addendum: 'Enviar Anexo',
				subcontract: 'Enviar Subcontrato'
			}
			return typeTexts[this.contractType]
		},
		signTooltip() {
			const typeTexts = {
				contract: 'El contrato ya ha sido firmado por el cliente, sólo falta la firma de la empresa / profesional',
				addendum: 'El anexo ya ha sido firmado por el cliente, sólo falta la firma de la empresa / profesional',
				subcontract: 'El subcontrato ya ha sido firmado por el contratista, sólo falta la firma de la empresa / profesional'
			}
			return typeTexts[this.contractType]
		},
		previewTooltip() {
			const typeTexts = {
				contract: 'Visualiza el formato final en PDF y envía al cliente',
				addendum: 'Visualiza el formato final en PDF y envía al cliente',
				subcontract: 'Visualiza el formato final en PDF y envía al contratista'
			}
			return typeTexts[this.contractType]
		},
		reviewObservationsText() {
			const typeTexts = {
				contract: 'Luego de revisar las observaciones del cliente previsualiza y envía nuevamente el contrato para su revisión',
				addendum: 'Luego de revisar las observaciones del cliente previsualiza y envía nuevamente el anexo para su revisión',
				subcontract: 'Luego de revisar las observaciones del contratista previsualiza y envía nuevamente el subcontrato para su revisión'
			}
			return typeTexts[this.contractType]
		},
		firstSendText() {
			const typeTexts = {
				contract: 'Previsualiza y envía el contrato al cliente para su revisión',
				addendum: 'Previsualiza y envía el anexo al cliente para su revisión',
				subcontract: 'Previsualiza y envía el subcontrato al contratista para su revisión'
			}
			return typeTexts[this.contractType]
		},
		pdfTooltip() {
			const typeTexts = {
				contract: 'Visualizar la última versión del contrato que fue convertida a PDF',
				addendum: 'Visualizar la última versión del anexo que fue convertida a PDF',
				subcontract: 'Visualizar la última versión del subcontrato que fue convertida a PDF'
			}
			return typeTexts[this.contractType]
		},
		contractAddendumsList(){
			if(this.deletedAddendumShow){
				return this.otherContractAddendums?.filter((item) => item.deleted_at) || []
			}
			return this.otherContractAddendums?.filter((item) => !item.deleted_at) || []
		},
		validatePayment() {
			if (!this.paymentValidation) return true;
			return this.paymentValidation.isValid;
			// return this.paymentValidation.isValid || this.paymentValidation.status === 'paid';
		},
		resetContractModalData() {
			return {
				title: 'Restablecer ' + this.contractTypeText,
				content_a: '¿Estás seguro de que deseas restablecer este ' + this.contractTypeText.toLowerCase() + '?',
				content_b: 'El ' + this.contractTypeText.toLowerCase() + ` volverá a su estado inicial y podrás modificarlo y enviarlo nuevamente al ${this.isSubcontract ? 'contratista' : 'cliente'} para su revisión.`,
			}
		},
		canShowObservationsButton() {
			return this.contract.last_status?.status >= this.contractStatuses.ACEPTADO
		}
	},
	mounted() {
		this.$nextTick(() => {
			this.initResizeObserver();
		});
	},
	methods: {
    initResizeObserver() {
      const el = this.$refs.contractSecondHeaderInfo?.$el || this.$refs.contractSecondHeaderInfo;

      if (!el) return;

      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }

      this.resizeObserver = new ResizeObserver(() => {
				this.$store.commit('contracts/setContractSecondHeaderHeight', el.offsetHeight)
      });

      this.resizeObserver.observe(el);
    },
		async updateContractStatus(status){
			await addNewStatus({ contract_id: this.contract.id, status })
			this.$emit('reload-contract-info')
		},
		openResetContractModal() {
			this.showResetContractModal = true
			this.pendingResetStatus = this.contractStatuses.CREADO
		},
		closeResetContractModal(confirmed = false) {
			if (confirmed && this.pendingResetStatus) {
				this.updateContractStatus(this.pendingResetStatus)
			}
			this.showResetContractModal = false
			this.pendingResetStatus = null
		},
		toProjectTable(event) {
			this.$router.push({ name: 'ProjectCostBreakdown', params: { id: this.contract.morphable?.id } })
			if (event.ctrlKey || event.metaKey || event.shiftKey || event.button === 1) {
				return;
			}

			event.preventDefault();

			const screenWidth = window.screen.width;
			const screenHeight = window.screen.height;
			const finalWidth = Math.min(1000, screenWidth);

			window.open(
				`/built-in-content?type=${this.contractType}&id=${this.contract.id}`,
				'',
				`width=${finalWidth},height=${screenHeight},left=${finalWidth},top=0,toolbar=no,menubar=no,location=no,status=no`
			);
		},
		toggleObservations() {
			this.$emit('toggle-observations')
		},
		previewContract() {
			this.$emit('preview-contract')
		},
		companyRepSign() {
			this.$emit('company-sign')
		},
		contractToPdf() {
			this.$emit('contract-to-pdf')
		},
		openContractPDF() {
			this.$emit('open-contract-pdf')
		},
		startContractAddendum() {
			this.$emit('start-addendum')
		},
		toggleDeletedAddendumShow() {
			this.deletedAddendumShow = !this.deletedAddendumShow
		},
		templateContentSelected(content) {
			this.$emit('template-selected', content)
		},
		startDeleteContract() {
			this.$emit('start-delete')
		},
		toParentContract() {
			this.$emit('to-parent-contract')
		},
		contractStatusColor(status) {
			return this.$options.filters?.contractStatusColor?.(status) || '#000'
		},
		getContractStatusText(status) {
			if (this.isSubcontract) {
				return this.$options.filters?.contractStatusSubcontractor?.(status) || '--'
			}
			return this.$options.filters?.contractStatus?.(status) || '--'
		},
	}
}
</script>

<style>
	/* .disable-all-buttons .v-btn {
		pointer-events: none;
		opacity: 0.6;
	} */
</style>